<template>
  <div class="purchases">
    <div class="top-bar">
      <button class="arrow-button" @click="dateDecr()">&#9664;</button>
      <p class="date">{{ date }}</p>
      <button class="arrow-button" @click="dateIncr()">&#9654;</button>
    </div>
    <div v-if="purchases">
      <h2>Proceeds: {{ Math.floor(totalProceeds) }}kr</h2>
      <div class="product-counts">
        <p class="product-count">{{pass10Count}}</p>
        <p class="product-count-label">10-day</p>
        |
        <p class="product-count">{{subMonthCount}}</p>
        <p class="product-count-label">Monthly</p>
        |
        <p class="product-count">{{subYearCount}}</p>
        <p class="product-count-label">Yearly</p>
      </div>

      <div class="product-counts">
        <p class="product-count">{{uniqueUserCount}}</p>
        <p class="product-count-label">Users</p>
        |
        <p class="product-count">{{Math.floor(totalProceeds / uniqueUserCount)}}</p>
        <p class="product-count-label">kr avg.</p>
      </div>
    </div>
    <div v-else>Loading data...</div>
    <div class="vertical-container">
      <MultipleChoiceToggle class="platform-toggle" :options="platformLabels" :onSelected="onPlatformSelected"/> 
      <MultipleChoiceToggle class="timeframe-toggle" :options="timeframeLabels" :onSelected="onTimeframeSelected"/> 
    </div>
    <div class="horizontal-container">
      <div class="chart">
        <p class="chart-title">Purchases</p>
        <TimelineChart ref="purchasesChart" :interval="timeframe" :labels="purchaseTypes" :endDate="endDate" :colors="purchaseColors"/>
      </div>
      <div class="chart">
        <p class="chart-title">Proceeds</p>
        <TimelineChart ref="revenueChart" :interval="timeframe" :labels="purchaseTypes" :endDate="endDate" :colors="purchaseColors"/>
      </div>
    </div>

    <div class="horizontal-container">
      <div class="chart">
        <p class="chart-title">Yearly Proceeds</p>
        <TimelineChart ref="yearlyRevenueChart" :interval="timeframe" :labels="purchaseTypes" :endDate="endDate" :colors="purchaseColors"/>
      </div>
      <!--div class="chart">
        <p class="chart-title">Time of Day</p>
        <TimeOfDayChart ref="purchasesTODChart" :labels="[purchaseTypes[0], purchaseTypes[2], purchaseTypes[4]]" :colors="[purchaseColors[0], purchaseColors[2], purchaseColors[4]]"/>
      </div>
      <div class="chart">
        <p class="chart-title">Day of Week</p>
        <HistogramChart ref="purchasesDOWChart" :tags="[purchaseTypes[0], purchaseTypes[2], purchaseTypes[4]]" :labels="['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun']"/>
      </div-->
    </div>
 
    <div class="horizontal-container">
      <div class="chart">
        <p class="chart-title">User Proceeds</p>
        
        <HistogramChart ref="userSpendChart" :interval="20" :start="0" :tags="['Active', 'Inactive']" />
        <div>
          <input type="checkbox" id="user-spend-checkbox" v-model="userSpendIncludeSingle10Day" />
          <label for="user-spend-checkbox">Include single 10-day</label>
        </div>
      </div>
      <div class="chart">
        <div class="chart-title-container">
          <p class="chart-title">User Months</p>
          <button class="chart-export-button" @click="copyMonthyRetention()">Export</button>
        </div>
        <HistogramChart ref="userMonthsChart"  :tags="['Cumulative', 'Current', 'Inactive']" :colors="[purchaseColors[0], purchaseColors[1], purchaseColors[2]]" />
        <div>
          <input type="checkbox" id="user-months-yearly-checkbox" v-model="userMonthsIncludeYearly" />
          <label for="user-months-yearly-checkbox">Include yearly</label>
        </div>
        
      </div>
    </div>

    <div class="repeat-purchases-container">
        <h2>Users with More than One Purchase:</h2>
        <div>
          <input type="checkbox" id="renewals-checkbox" v-model="multiplePurchaseIncludeRenewals" />
          <label for="renewals-checkbox">Include renewals</label>
        </div>
        <ul>
          <template v-for="user in getMultiPurchaseUsers()" :key="user.userId">
            <h3>{{ user.userId }}</h3>
            <ul>
              <li v-for="purchase in user.purchases" :key="purchase.timestamp">
                <p>{{ formatDate(new Date(purchase.timestamp), 'hour') }}: {{ purchase.productId }} {{ purchase.isRenewal ? "(renewal)" : "" }}</p>
              </li>
            </ul>
          </template>
        </ul>
      </div>
  </div>

  <div>
    Invalid purchases
    {{invalidPurchases}}
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { PurchaseData, PurchaseDataRaw } from '../interfaces/PurchaseData'
import TimelineChart, { TimelineChartEvent } from '../components/TimelineChart.vue'
import TimeOfDayChart, { TimeOfDayChartEvent } from '../components/TimeOfDayChart.vue'
import MultipleChoiceToggle from '../components/MultipleChoiceToggle.vue'
import * as fb from '../firebase'
import { nextTick, watch } from 'vue'
import { parseQuery, useRoute, useRouter } from 'vue-router'
import HistogramChart, { HistogramChartItem } from '@/components/HistogramChart.vue'
import * as conf from '../config'

@Options({
  props: {
    dataFile: String
  },
  components: {
    TimelineChart,
    TimeOfDayChart,
    MultipleChoiceToggle,
    HistogramChart,
  },
})

export default class PurchasesView extends Vue {
  invalidPurchases: string[] = [
    '2024-04-10--Nv7facPvLyr_poMIhok', // duplicate yearly sub

    // '2024-04-28--Nw_YBFHhYagFH7tlQbL', // permanent premium
    // '2024-04-30--Nwhgsy35B-OZN5NkyGV', // permanent premium (same user) F1JD66pZeFPa9eiQWIrjEkvRFom2
    // '2024-05-24--NydNjebjOfG8C9KZW1F', // permanent premium (same user)

    // '2024-05-06--NxETGFcHjaWwAx_ziDP', // Transaction regisetered three times, last two with same transactionId
    // '2024-05-06--NxETGFjJvNIC_mRVw5x', // Same transaction as above, but user seems to have made two purchases so adding this back in

    '2025-03-09--OKwGxKSu6-Pc4q60s94', // Same transaction as, but different id (2025-03-09--OKwFaYayy7TuKrN-rbn)
    '2025-03-30--OMaYFgvBHZztoJK6EFC', // Same transaction as, but different id (2025-03-30--OMaYB24KEKD5nD93GnS)
    '2025-04-15--ONtyARO4uFl1-v4O4ES', // Same transaction as, but different id (2025-04-15--ONty09GmDpOQ1s5lbsS)
    '2025-04-15--ONty4oTfaxZ-VFSCmrB', // Same transaction as, but different id (2025-04-15--ONty09GmDpOQ1s5lbsS)

    '2025-08-22--OYGyZlG-1c21leRQVFz', // Samuel transaction, maybe from dev environment?
    '2025-08-22--OYGyZrFKnFMDUQ7OHl5', // Samuel transaction, maybe from dev environment?
    '2025-08-22--OYGyZuDpVzOFgEq2XAH', // Samuel transaction, maybe from dev environment?
  ]

  purchaseColors = ['#002dbf', '#1a4fff', '#99004d', '#ff0080', '#00a69d']
  purchaseTypes = ['Monthly', 'Monthly Renewal', 'Yearly', 'Yearly Renewal', '10-day']
  platformLabels = ['All', 'Apple', 'Android']
  timeframeLabels = ['30 days', '1 year', 'All']
  date = ''

  timeframe: string = ''
  platforms: string[] = []
  startDate: Date = new Date()
  endDate: Date = new Date()
  isUtc: boolean = false
  multiplePurchaseIncludeRenewals: boolean = false
  userSpendIncludeSingle10Day: boolean = false
  userMonthsIncludeYearly: boolean = false

  dataFile!: string
  allPurchases: PurchaseData[] = []
  purchases: PurchaseData[] = []

  totalProceeds: number = 0.0
  subMonthCount = 0
  subYearCount = 0
  pass10Count = 0
  uniqueUserCount = 0
  userPurchases: {
    [userId: string]: PurchaseData[]
  } = {}

  yearlyProceeds: {
    [date: string]: {
      [type: string]: {
        [platform: string]: number,
      }
    }
  } = {}

  purchasesChart: TimelineChart | null = null
  revenueChart: TimelineChart | null = null
  yearlyRevenueChart: TimelineChart | null = null
  purchasesTODChart: TimeOfDayChart | null = null
  purchasesDOWChart: HistogramChart | null = null
  userSpendChart: HistogramChart | null = null
  userMonthsChart: HistogramChart | null = null
  
  unsubPurchases: (() => void) | null = null
  
  beforeMount() {
    const route = useRoute()
    const router = useRouter()

    if (route.query.date) {
      this.date = route.query.date as string
    } else {
      this.setDate(new Date())
      /*
      const query = { ...(route.query as { [key: string]: any }), date: this.date }
      router.push({ query })
      */
    }

    if (route.query.timeZone === 'utc') {
      this.isUtc = true
    }

  }
  
  async mounted() {
    this.purchasesChart = this.$refs.purchasesChart as TimelineChart
    this.revenueChart = this.$refs.revenueChart as TimelineChart
    this.yearlyRevenueChart = this.$refs.yearlyRevenueChart as TimelineChart
    this.purchasesTODChart = this.$refs.purchasesTODChart as TimeOfDayChart
    this.purchasesDOWChart = this.$refs.purchasesDOWChart as HistogramChart
    this.userSpendChart = this.$refs.userSpendChart as HistogramChart
    this.userMonthsChart = this.$refs.userMonthsChart as HistogramChart

    this.unsubPurchases = fb.observe('iap/purchases', (snapshot) => {
      this.update(snapshot.val())
    }, (error) => {
      console.log(error)
    })

    // await this.loadFileData()

    watch(() => this.userSpendIncludeSingle10Day, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        this.updateUserSpendChart()
      }
    })

    watch(() => this.userMonthsIncludeYearly, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        this.updateUserMonthsChart()
      }
    })
    
  }

  beforeUnmount() {
    if (this.unsubPurchases) {
      this.unsubPurchases()
    }
  }

  async onTimeframeSelected(choice: number) {
    switch (choice) {
      case 0:
        this.timeframe = 'day'
        break
      case 1:
        this.timeframe = 'week'
        break
      case 2:
        this.timeframe = 'month'
        break
    }
    this.updateDateRange()
    await nextTick()
    this.analyzePurchases()
    this.updateCharts()
  }

  async onPlatformSelected(choice: number) {
    switch (choice) {
      case 0:
        this.platforms = ['apple', 'google']
        break
      case 1:
        this.platforms = ['apple']
        break
      case 2:
        this.platforms = ['google']
        break
    }
    this.analyzePurchases()
    this.updateCharts()
  }

  async dateDecr() {
    const date = new Date(this.date)
    date.setDate(date.getDate() - 1)
    this.setDate(date)
    this.updateDateRange()
    await nextTick()
    this.analyzePurchases()
    this.updateCharts()
  }

  async dateIncr() {
    const date = new Date(this.date)
    date.setDate(date.getDate() + 1)
    this.setDate(date)
    this.updateDateRange()
    await nextTick()
    this.analyzePurchases()
    this.updateCharts()
  }

  async copyMonthyRetention() {
    const exportData = []

    for (const userId of Object.keys(this.userPurchases)) {
      const allPurchases = this.userPurchases[userId]

      let isActive = false
      const monthPurchases = allPurchases.filter((purchase) => { return this.getProduct(purchase.productId) === 'sub-month' })
      const months = monthPurchases.length
      
      if (months === 0) {
        continue
      }
      
      if (this.isActive(allPurchases[allPurchases.length - 1])) {
        isActive = true
      }
      
      for (let i = 0; i < months - 1; i++) {
        if (!exportData[i]) {
          exportData[i] = [0, 0, 0]
        }
        
        exportData[i][0] += 1
      }
      
      if (!isActive) {
        exportData[months - 1][1] += 1
      }

      const monthsPotential = this.monthsElapsed(monthPurchases[0].timestamp, Date.now())
      for (let i = 0; i < monthsPotential; i++) {
        if (!exportData[i]) {
          exportData[i] = [0, 0, 0]
        }

        exportData[i][2] += 1
      }
      
    }

    navigator.clipboard.writeText(exportData.join('\n'))
  }

  setDate(date: Date) {
    const year = date.getFullYear().toString()
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)
    this.date = `${year}-${month}-${day}`
  }

  async loadFileData() {
    try {
      const response = await fetch(this.getDataFilePath())
      if (!response.ok) {
        throw new Error('Failed to load data')
      }
      const data = await response.text()

      const dataObject = JSON.parse(data)
      this.update(dataObject)
      
    } catch (error) {
      console.error(error)
      // Handle error here
    }
  }

  update(purchasesData: any) {
    this.allPurchases = []
    this.userPurchases = {}
    this.yearlyProceeds = {}

    for (const purchaseId of Object.keys(purchasesData)) {
      if (this.invalidPurchases.includes(purchaseId)) {
        continue
      }
      
      const purchaseRaw = purchasesData[purchaseId] as PurchaseDataRaw

      let platformId: string = ''
      let platformReceipt: string = ''
      if (purchaseRaw.platform_data.transaction_id) {
        platformId = purchaseRaw.platform + '-' + purchaseRaw.platform_data.transaction_id
      } else if (purchaseRaw.platform_data.order_id) {
        platformId = purchaseRaw.platform + '-' + purchaseRaw.platform_data.order_id
      }

      if (purchaseRaw.platform_data.receipt) {
        platformReceipt = purchaseRaw.platform + '-' + purchaseRaw.platform_data.receipt
      } else if (purchaseRaw.platform_data.purchase_token) {
        platformReceipt = purchaseRaw.platform + '-' + purchaseRaw.platform_data.purchase_token
      }

      if (this.getProduct(purchaseRaw.product_id) === 'missing') {
        this.invalidPurchases.push(purchaseId)
        continue
      }

      // Remove duplicates based on platformId
      if (this.allPurchases.findIndex((p) => { return p.platformId === platformId }) !== -1) {
        this.invalidPurchases.push(purchaseId)
        continue
      }
      
      const purchase: PurchaseData = {
        timestamp: purchaseRaw.timestamp,
        platform: purchaseRaw.platform,
        productId: purchaseRaw.product_id,
        proceeds: this.getProceedsEstimate(purchaseRaw.product_id),
        userId: purchaseRaw.user_id,
        isRenewal: false,
        isReactivate: false,
        platformId: platformId,
        platformReceipt: platformReceipt,
      }

      if (conf.excludedUsers.includes(purchase.userId)) {
        continue
      }

      this.allPurchases.push(purchase)

      if (!this.userPurchases[purchase.userId]) {
        this.userPurchases[purchase.userId] = []
      }
      
      if (this.getProduct(purchase.productId).includes('sub')) {
        var isReactivate = false
        var isRenewal = false
        for (const userPurchase of this.userPurchases[purchase.userId]) {
          if (userPurchase.productId === purchase.productId) {
            isReactivate = true
            if (purchase.productId.includes('yearly')) {
              isRenewal = true
            } else if (purchase.productId.includes('monthly') && purchase.timestamp - userPurchase.timestamp < 35 * 24 * 60 * 60 * 1000) {
              isRenewal = true
            }
          }
        }

        if (isRenewal) {
          purchase.isRenewal = true
        } else if (isReactivate) {
          purchase.isReactivate = true
        }
      }

      this.userPurchases[purchase.userId].push(purchase)
    }

    // Update yearly proceeds
    for (const purchase of this.allPurchases) {
      const value = this.getProceedsEstimate(purchase.productId)
      for (let i = 0; i < 365; i++) {
        const date = this.toDate(purchase.timestamp + i * 24 * 60 * 60 * 1000)
        const key = date.toISOString().split('T')[0]

        if (!this.yearlyProceeds[key]) {
          this.yearlyProceeds[key] = {}
          this.purchaseTypes.forEach(element => {
            // TODO: Add platforms dynamically
            this.yearlyProceeds[key][element] = {
              google: 0,
              apple: 0,
            }
          })
        }

        this.yearlyProceeds[key][this.getTag(purchase)][purchase.platform] += value
      }
    }

    this.analyzePurchases()
    this.updateCharts()
  }

  updateDateRange() {
    const endDate = new Date(this.date)
    endDate.setHours(23, 59, 59, 999)

    let startDate = new Date(this.date)
    startDate.setHours(0, 0, 0, 0)
    switch (this.timeframe) {
      case 'day':
        startDate.setDate(startDate.getDate() - 29)
        break
      case 'week':
        startDate.setDate(startDate.getDate() - 364)
        break
      case 'month':
        startDate = new Date('2024-01-01 00:00:00')
        break
    }
    this.startDate = startDate
    this.endDate = endDate
  }

  analyzePurchases() {
    this.purchases = []
    this.totalProceeds = 0.0
    this.subMonthCount = 0
    this.subYearCount = 0
    this.pass10Count = 0
    this.uniqueUserCount = 0

    for (const purchase of this.allPurchases) {
      if (!this.platforms.includes(purchase.platform)) {
        continue
      }
      if (purchase.timestamp < this.startDate.getTime()) {
        continue
      }
      if (purchase.timestamp > this.endDate.getTime()) {
        continue
      }

      this.purchases.push(purchase)
    }

    const users: string[] = []
    for (const purchase of this.purchases) {
      if (!users.includes(purchase.userId)) {
        this.uniqueUserCount++
        users.push(purchase.userId)
      }
      
      this.totalProceeds += purchase.proceeds

      switch (this.getProduct(purchase.productId)) {
        case 'pass-10':
          this.pass10Count++
          break
        case 'sub-month':
          this.subMonthCount++
          break
        case 'sub-year':
          this.subYearCount++
          break
        default:
          break
      }
    }
  }

  updateCharts() {

    const purchasesChartData: TimelineChartEvent[] = []
    for (const purchase of this.purchases) {
      const value = 1
      // const value = this.getProceedsEstimate(purchase.productId)
      purchasesChartData.push({
        type: this.getTag(purchase),
        date: this.toDate(purchase.timestamp),
        value: value,
      })
    }
    this.purchasesChart?.setData(purchasesChartData)

    const revenueChartData: TimelineChartEvent[] = []
    for (const purchase of this.purchases) {
      const value = this.getProceedsEstimate(purchase.productId)
      revenueChartData.push({
        type: this.getTag(purchase),
        date: this.toDate(purchase.timestamp),
        value: value,
      })
    }
    this.revenueChart?.setData(revenueChartData)

    const purchasesTODChartData: TimeOfDayChartEvent[] = []
    for (const purchase of this.purchases) {
      const value = 1
      purchasesTODChartData.push({
        type: this.getTag(purchase),
        date: this.toDate(purchase.timestamp),
        value: value,
      })
    }
    this.purchasesTODChart?.setData(purchasesTODChartData)

    const dowChartData: HistogramChartItem[] = []
    for (const purchase of this.purchases) {
      const dow = this.toDate(purchase.timestamp).getDay()
      dowChartData.push({
        type: this.getTag(purchase),
        value: (7 + dow - 1) % 7,
      })
    }
    
    this.purchasesDOWChart?.setData(dowChartData)

    this.updateUserSpendChart()
    this.updateUserMonthsChart()
    this.updateYearlyRevenueChart()
  }
  
  updateYearlyRevenueChart() {

    const firstYearDate = new Date('2025-03-31')
    let startDate = this.startDate
    if (startDate < firstYearDate) {
      startDate = firstYearDate
    }
    
    const todayStr = new Date().toISOString().split('T')[0]
    const revenueChartData: TimelineChartEvent[] = []
    for (const dateStr of Object.keys(this.yearlyProceeds)) {
      const dateData = this.yearlyProceeds[dateStr]
      const date = new Date(dateStr)
      
      if (date < startDate || date.getTime() > Date.now()) {
        continue
      }

      if (dateStr !== todayStr) {
        if (this.timeframe === 'week') {
          if (date.getDay() !== 0) {
            continue
          }
        } else if (this.timeframe === 'month') {
          if (date.getDate() < 28) {
            continue
          }

          const nextDayDate = new Date(date)
          nextDayDate.setDate(date.getDate() + 1)
          
          if (nextDayDate.getDate() !== 1) {
            continue
          }
        }
      }

      for (const tag of Object.keys(dateData)) {
        let value = 0
        this.platforms.forEach(platform => {
          value += dateData[tag][platform]
        })

        revenueChartData.push({
          type: tag,
          date: date,
          value: value,
        })
      }
    }

    this.yearlyRevenueChart?.setData(revenueChartData)
  }

  updateUserSpendChart() {
    const userSpendChartData: HistogramChartItem[] = []
    for (const userId of Object.keys(this.userPurchases)) {
      const allPurchases = this.userPurchases[userId]
      if (!this.userSpendIncludeSingle10Day) {
        if (allPurchases.length === 1 && this.getProduct(allPurchases[0].productId) === 'pass-10') {
          continue
        }
      }
      
      let isActive = false
      let totalSpend = 0
      for (const purchase of allPurchases) {
        totalSpend += purchase.proceeds
        if (this.isActive(purchase)) {
          isActive = true
        }
      }

      if (totalSpend === 0) {
        console.log(userId)
      }

      userSpendChartData.push({
        type: isActive ? 'Active' : 'Inactive',
        value: totalSpend,
      })
    }
    this.userSpendChart?.setData(userSpendChartData)
  }

  updateUserMonthsChart() {
    const userMonthsChartData: HistogramChartItem[] = []
    for (const userId of Object.keys(this.userPurchases)) {
      const allPurchases = this.userPurchases[userId]

      let isActive = false
      const months = allPurchases.filter((purchase) => { return this.getProduct(purchase.productId) === 'sub-month' }).length
      const years = allPurchases.filter((purchase) => { return this.getProduct(purchase.productId) === 'sub-year' }).length

      if (this.isActive(allPurchases[allPurchases.length - 1])) {
        isActive = true
      }
    
      let total = months
      if (this.userMonthsIncludeYearly) {
        total += years * 12
      }

      for (let i = 1; i <= total; i++) {
        const isLastMonth = i === total
        let type = 'Cumulative'
        if (isLastMonth) {
          type = isActive ? 'Current' : 'Inactive'
        }
        userMonthsChartData.push({
          type: type,
          value: i,
        })
      }
    }
    this.userMonthsChart?.setData(userMonthsChartData)
  }

  toDate(timestamp: number) {
    if (this.isUtc) {
      return this.localToUTC(new Date(timestamp))
    }
    return new Date(timestamp)
  }

  localToUTC(date: Date) {
    const utcTime = date.getTime() + (date.getTimezoneOffset() * 60000)
    return new Date(utcTime)
  }

  getMultiPurchaseUsers() {
    const result: {
      userId: string,
      purchases: PurchaseData[]
    }[] = []

    for (const userId of Object.keys(this.userPurchases)) {
      const allPurchases = this.userPurchases[userId]
      const nonRenewals = this.userPurchases[userId].filter((p) => { return !p.isRenewal })

      const purchases = this.multiplePurchaseIncludeRenewals ? allPurchases : nonRenewals
      if (purchases.length > 1) {
        result.push({
          userId: userId,
          purchases: purchases.sort((p0, p1) => { return p1.timestamp - p0.timestamp }),
        })
      }
    }

    return result.sort((u0, u1) => { return u1.purchases[0].timestamp - u0.purchases[0].timestamp })
  }

  getDataFilePath(): string {
    // Assuming dataFile is relative to the public directory
    return process.env.BASE_URL + 'data/' + this.dataFile
  }

  getProduct(productId: string) {
    switch (productId) {
      case 'se.hippsomapp.gpsorienteringrun.iap.premium10days':
        return 'pass-10'
      case 'se.hippsomapp.gpsorienteringrun.sub.premiummonthly':
        return 'sub-month'
      case 'se.hippsomapp.gpsorienteringrun.sub.premiumyearly':
        return 'sub-year'
      case 'se.hippsomapp.gpsorientering.iap.premium10days':
        return 'pass-10'
      case 'se.hippsomapp.gpsorientering.sub.premium:gpso-sub-monthly':
        return 'sub-month'
      case 'se.hippsomapp.gpsorientering.sub.premium:gpso-sub-yearly':
        return 'sub-year'
      default:
        console.log(`Missing product: ${productId}`)
        return 'missing'
    }
  }

  getProceedsEstimate(productId: string): number {
    /*
    const platformTakeRate = 0.15
    const avgVAT = 0.2
    
    const proceedsRate = 1 - platformTakeRate - avgVAT
    */
    let proceedsRate = 0.86 // empirical (includes VAT, platform take and currency conversion)
    proceedsRate *= 0.97 // prefer low estimate

    switch (this.getProduct(productId)) {
      case 'pass-10':
        return 19 * proceedsRate
      case 'sub-month':
        return 39 * proceedsRate
      case 'sub-year':
        return 349 * proceedsRate
      default:
        console.log(`Missing proceeds estimate: ${productId}`)
        return 0.0
    }
  }

  getTag(purchase: PurchaseData): string {
    switch (this.getProduct(purchase.productId)) {
      case 'pass-10':
        return this.purchaseTypes[4]
      case 'sub-month':
        return purchase.isRenewal ? this.purchaseTypes[1] : this.purchaseTypes[0]
      case 'sub-year':
        return purchase.isRenewal ? this.purchaseTypes[3] : this.purchaseTypes[2]
      default:
        console.log(`Missing tag: ${purchase.productId}`)
        return 'missing'
    }
    
  }

  isActive(purchase: PurchaseData): boolean {

    const timestamp = Date.now()

    switch (this.getProduct(purchase.productId)) {
      case 'pass-10':
        return timestamp - purchase.timestamp < 10 * 24 * 60 * 60 * 1000
      case 'sub-month':
        return timestamp - purchase.timestamp < 31 * 24 * 60 * 60 * 1000
      case 'sub-year':
        return timestamp - purchase.timestamp < 366 * 24 * 60 * 60 * 1000
      default:
        console.log(`Missing tag: ${purchase.productId}`)
        return false
    }
  }

  formatDate(date: Date, interval: string) {
    const year = date.getFullYear().toString().slice(-2)
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)
    const hours = ('0' + date.getHours()).slice(-2)

    switch (interval) {
      case 'day':
        return `${year}-${month}-${day}`
      case 'hour':
        return `${year}-${month}-${day} ${hours}:00`
    }
    
    return `${year}-${month}-${day} ${hours}:00`
  }

  monthsElapsed(fromTimestamp: number, toTimestamp: number) {
    const dateFrom = new Date(fromTimestamp)
    const dateTo = new Date(toTimestamp)
    var yearsDifference = dateTo.getFullYear() - dateFrom.getFullYear()
    var monthsDifference = dateTo.getMonth() - dateFrom.getMonth()
    var daysDifference = dateTo.getDate() - dateFrom.getDate()

    // If the day of the month in dateTo is before the day of the month in dateFrom,
    // it means we haven't reached a whole month yet, so we subtract one month
    if (daysDifference <= 0) {
      monthsDifference -= 1
    }

    const months = (yearsDifference * 12) + monthsDifference
    console.log(`${dateFrom} - ${dateTo}: ${months}`)
    return months
  }
}
</script>

<style scoped>
.purchases {
  padding-top: 10px;
}

.top-bar {
  display: flex;
  justify-content: flex-end;
  padding-left: 30px;
  padding-right: 30px;
}

.arrow-button {
  border: none;
  background: none;
  font-size: 16px; /* Adjust font size as needed */
  cursor: pointer;
  margin-top: -4px;
  color: white; /* Set the color to white */
}

.product-counts {
  margin-top: -5px;
  margin-bottom: 15px;
}

.product-count {
  display: inline;
  margin-left: 15px;
  font-size: larger;
}
.product-count-label {
  display: inline;
  margin-left: 6px;
  margin-right: 15px;
  font-size: small;
}

.platform-toggle {
  margin-top: 15px;
  justify-content: center;
}

.timeframe-toggle {
  margin-top: 15px;
  width: 350px;
  justify-content: center;
}

.vertical-container {
  display: flex;
  flex-direction: column;
  justify-content: center; /* Vertically center */
  align-items: center; /* Horizontally center */
}

.repeat-purchases-container {
  margin-top: 100px;
}

.chart {
    align-items: center;
    display: flex;
    flex-direction: column;
}

.chart-title {
  margin-top: 20px;
  margin-bottom: 0px;
}

.chart-title-container {
  display: flex;
  flex-direction: row;
  align-items: end;
  justify-content: space-between;
  width: 90%;
}

.chart-export-button {
  align-self: flex-end;
}

/* Desktop */
.desktop-view .platform-toggle {
  width: 500px;
}

.desktop-view .horizontal-container {
  display: flex;
  justify-content: center;
}

.desktop-view .chart {
  max-width: 600px;
  flex: 1;
  padding: 0px 40px;
}

/* Mobile */
.mobile-view .horizontal-container {
  display: flex;
  flex-direction: column;
  align-items: center; /* Horizontally center */
}

.mobile-view .chart {
  width: 95%;
  flex: 1;
  padding: 0px 00px;
}

.mobile-view .platform-toggle {
  width: 350px;
}

.mobile-view .repeat-purchases-container {
  margin-top: 100px;
  scale: .7;
}
</style>
